/**
 * ChatMessage — one turn in a chat thread, styled like current AI chat apps: the assistant's
 * reply sits on the page as plain text, the user's message in a soft bubble on the right.
 * A failed assistant turn renders as an error Alert. `footer` hangs extra content (e.g. source
 * links) under an assistant reply, and `copyable` adds a copy button under it, as other AI
 * chats do. Shared by CareStart's typed chat and the Medical Library.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { Alert, Box, Fade, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

function CopyButton({ text }) {
  const [state, setState] = useState('idle'); // idle | copied | failed

  useEffect(() => {
    if (state === 'idle') return undefined;
    const t = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(t);
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
  };

  const label = state === 'copied' ? 'Copied' : state === 'failed' ? "Couldn't copy" : 'Copy';
  return (
    <Tooltip title={label}>
      <IconButton size="small" aria-label={label} onClick={copy} sx={{ color: 'text.secondary' }}>
        {state === 'copied' ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

CopyButton.propTypes = { text: PropTypes.string.isRequired };

const markdownSx = {
  '& p': { m: 0 },
  '& p + p': { mt: 1.5 },
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
};

function ChatMessage({ role, text, error = false, footer = null, copyable = false }) {
  const isUser = role === 'user';
  const body = (
    <Typography variant="body1" component="div" sx={{ lineHeight: 1.6 }}>
      <ReactMarkdown>{text}</ReactMarkdown>
    </Typography>
  );

  return (
    <Fade in timeout={250}>
      <Box
        sx={{
          alignSelf: isUser ? 'flex-end' : 'stretch',
          maxWidth: isUser ? '80%' : '100%',
          ...(isUser && { bgcolor: 'action.selected', borderRadius: 5, px: 2, py: 1.25 }),
          ...markdownSx,
        }}
      >
        {error ? <Alert severity="error">{body}</Alert> : body}
        {footer}
        {copyable && !isUser && !error && text ? (
          // Pulled left so the icon lines up with the text above it.
          <Box sx={{ mt: 0.5, ml: -0.75 }}>
            <CopyButton text={text} />
          </Box>
        ) : null}
      </Box>
    </Fade>
  );
}

ChatMessage.propTypes = {
  role: PropTypes.oneOf(['user', 'assistant']).isRequired,
  text: PropTypes.string,
  error: PropTypes.bool,
  footer: PropTypes.node,
  copyable: PropTypes.bool,
};

export default ChatMessage;
